import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { Loader } from 'src/components/atoms/Loader';
import Section from 'src/components/atoms/Section/Section';
import { ShowState } from 'src/components/molecules/ShowState';
import { Onepage } from 'src/components/templates';
import { selectors as authSelectors } from 'src/features/auth/authSlice';
import useNavOptionPermissionsWithLoading from 'src/hooks/useNavOptionPermissionsWithLoading';
import { LayoutPage } from 'src/pages';
import { Status } from 'src/ts/enums';

type Props = {
  component: FC | React.ReactNode;
  routeName: string;
  template?: 'LayoutPage' | 'Onepage' | 'WidePage';
};

const PrivateRouteWithFeatureFlags = ({
  component: Component,
  routeName,
  template = 'LayoutPage',
  ...rest
}: Props): JSX.Element => {
  const navigate = useNavigate();
  const user = useSelector(authSelectors.user);
  const userStatus = useSelector(authSelectors.status);
  const isAuthorized = Boolean(user?.email) && userStatus === Status.SUCCESS;

  const { permissions, isLoading } = useNavOptionPermissionsWithLoading(routeName);

  useEffect(() => {
    if (!routeName) {
      throw new Error('PrivateRouteWithFeatureFlags Component: you must define routeName: string');
    }
  }, [routeName]);

  // Show loading while user authentication or feature flags are loading
  if (userStatus === Status.LOADING || isLoading) {
    return <Loader isFixed />;
  }

  // Redirect to login if not authenticated
  if ((userStatus === Status.SUCCESS || userStatus === Status.ERROR) && !user) {
    return <Navigate to="/login" />;
  }

  // Check permissions after everything is loaded
  const hasPermission =
    user &&
    isAuthorized &&
    Array.isArray(permissions) &&
    permissions.length > 0 &&
    permissions.includes(user.role);

  const PrintPage = () => {
    if (template === 'Onepage') {
      return (
        <Onepage>{typeof Component === 'function' ? <Component {...rest} /> : Component}</Onepage>
      );
    }

    return (
      <LayoutPage isWideRoute={template === 'WidePage'}>
        {typeof Component === 'function' ? <Component {...rest} /> : Component}
      </LayoutPage>
    );
  };

  return hasPermission ? (
    PrintPage()
  ) : (
    <Section>
      <ShowState
        variant="error"
        type="information"
        buttonLabel="Go to Login"
        message="You do not have access to this page"
        onClick={() => navigate('/login')}
      />
    </Section>
  );
};

export default PrivateRouteWithFeatureFlags;
